if (!customElements.get('digital-coupon-form')) {
  customElements.define(
    'digital-coupon-form',
    class DigitalCouponForm extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.initMotivPreview();
        this.initAmountField();
      }

      disconnectedCallback() {
        this.motivSelect?.removeEventListener('change', this.onMotivChange);
        this.amountInput?.removeEventListener('input', this.onAmountInput);
      }

      // ---- Motiv -> matching product image (see snippet comment: match by image alt text) ----
      initMotivPreview() {
        this.motivSelect = this.querySelector('[data-digital-coupon-motiv]');
        this.mediaMapScript = this.querySelector('[data-digital-coupon-motiv-media]');
        if (!this.motivSelect || !this.mediaMapScript) return;

        try {
          this.mediaMap = JSON.parse(this.mediaMapScript.textContent);
        } catch (error) {
          this.mediaMap = {};
        }

        this.onMotivChange = this.onMotivChange.bind(this);
        this.motivSelect.addEventListener('change', this.onMotivChange);
      }

      onMotivChange() {
        const media = this.mediaMap[this.motivSelect.value];
        if (!media) return;

        const setActiveMedia = () => {
          const gallery = document.querySelector(`[id^="MediaGallery-${this.sectionId}"]`);
          if (gallery && typeof gallery.setActiveMedia === 'function') {
            gallery.setActiveMedia({ featured_media: media });
            return true;
          }
          return false;
        };

        if (!setActiveMedia()) {
          let attempts = 0;
          const timer = setInterval(() => {
            attempts += 1;
            if (setActiveMedia() || attempts > 10) clearInterval(timer);
          }, 100);
        }
      }

      // ---- Custom amount: the product carries one real, pre-created variant per whole-euro
      // amount (see scripts/setup-digital-coupon-variants.py and the snippet comment). The
      // {amount: variantId} map is baked into the page, so picking an amount is a plain local
      // lookup — no network call, nothing to wait on. ----
      initAmountField() {
        this.amountInput = this.querySelector('[data-digital-coupon-amount]');
        this.errorMessage = this.querySelector('[data-digital-coupon-amount-error]');
        this.variantPricesScript = this.querySelector('[data-digital-coupon-variant-prices]');
        this.form = this.closest('form');
        if (!this.amountInput || !this.form) return;

        this.minAmount = Number(this.dataset.minAmount);
        this.maxAmount = Number(this.dataset.maxAmount);

        try {
          this.variantPrices = JSON.parse(this.variantPricesScript?.textContent || '{}');
        } catch (error) {
          this.variantPrices = {};
        }

        this.variantIdInput = this.form.querySelector('[name="id"]');
        this.submitButton = this.form.querySelector('[type="submit"]');

        this.onAmountInput = this.onAmountInput.bind(this);
        this.amountInput.addEventListener('input', this.onAmountInput);

        this.setSubmitReady(false);
      }

      // Whole numbers only — strip anything typed that isn't a digit.
      sanitizeAmountInput() {
        const digitsOnly = this.amountInput.value.replace(/\D/g, '');
        if (digitsOnly !== this.amountInput.value) this.amountInput.value = digitsOnly;
      }

      onAmountInput() {
        this.sanitizeAmountInput();
        this.setError(false);

        const raw = this.amountInput.value;
        if (!raw) {
          this.setSubmitReady(false);
          this.setPriceVisible(false);
          return;
        }

        const variantId = this.variantPrices[raw];
        if (!variantId) {
          this.setSubmitReady(false);
          this.setPriceVisible(false);
          const amount = Number(raw);
          if (amount >= this.minAmount && amount <= this.maxAmount) this.setError(true);
          return;
        }

        if (this.variantIdInput) this.variantIdInput.value = variantId;
        this.updatePriceDisplay(Number(raw));
        this.setSubmitReady(true);
      }

      updatePriceDisplay(amount) {
        if (typeof FoxTheme === 'undefined') return;
        const priceContainer = document.getElementById(`price-${this.sectionId}`);
        if (!priceContainer) return;

        const formatted = FoxTheme.Currency.formatMoney(amount * 100, FoxTheme.settings.moneyFormat);
        priceContainer.querySelectorAll('.f-price-item--regular, .f-price-item--sale').forEach((el) => {
          el.innerHTML = formatted;
        });
        priceContainer.hidden = false;
      }

      setPriceVisible(visible) {
        const priceContainer = document.getElementById(`price-${this.sectionId}`);
        if (priceContainer) priceContainer.hidden = !visible;
      }

      setSubmitReady(ready) {
        if (!this.submitButton) return;
        this.submitButton.disabled = !ready;
      }

      setError(show) {
        if (!this.errorMessage) return;
        this.errorMessage.toggleAttribute('hidden', !show);
        if (show) this.errorMessage.textContent = this.dataset.amountErrorText || '';
      }
    }
  );
}
